import { useState } from 'react';

interface Client {
  id:        string;
  full_name: string;
  email:     string | null;
  phone:     string | null;
}

interface Props {
  clients:   Client[];
  loading:   boolean;
  onSelect:  (client: Client) => void;
  onClose:   () => void;
}

export default function LinkClientModal({ clients, loading, onSelect, onClose }: Props) {
  const [query, setQuery] = useState('');

  const filtered = clients.filter((c) => {
    const q = query.toLowerCase();
    return (
      c.full_name.toLowerCase().includes(q) ||
      (c.email ?? '').toLowerCase().includes(q) ||
      (c.phone ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-md flex flex-col overflow-hidden"
        style={{ maxHeight: '520px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
              <i className="ri-user-search-line text-[#C8A96E] text-base" />
            </div>
            <div>
              <h3 className="text-[#0f172a] font-semibold text-sm">Link to Existing Client</h3>
              <p className="text-slate-400 text-[11px]">Select a client from your CRM</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, phone…"
              autoFocus
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 focus:bg-white transition-all"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto py-1">
          {loading ? (
            <div className="space-y-2 p-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center px-4">
              <i className="ri-user-search-line text-slate-200 text-3xl mb-2" />
              <p className="text-slate-400 text-sm font-medium mb-1">
                {query ? 'No clients match your search' : 'No clients found'}
              </p>
              <p className="text-slate-300 text-xs">
                {query ? 'Try a different name or email.' : 'Add clients in the Clients section first.'}
              </p>
            </div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onSelect(c)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors cursor-pointer text-left group"
              >
                <div
                  className="w-9 h-9 flex items-center justify-center rounded-full text-white text-xs font-bold shrink-0"
                  style={{ backgroundColor: '#C8A96E' }}
                >
                  {c.full_name[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#0f172a] text-sm font-medium truncate">{c.full_name}</p>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    {c.email && (
                      <span className="text-slate-400 text-[11px] flex items-center gap-1 truncate max-w-[160px]">
                        <i className="ri-mail-line text-[10px]" />
                        {c.email}
                      </span>
                    )}
                    {c.phone && (
                      <span className="text-slate-400 text-[11px] flex items-center gap-1">
                        <i className="ri-phone-line text-[10px]" />
                        {c.phone}
                      </span>
                    )}
                  </div>
                </div>
                <i className="ri-arrow-right-s-line text-slate-300 group-hover:text-[#C8A96E] transition-colors shrink-0" />
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
          <p className="text-slate-400 text-xs">
            {filtered.length} client{filtered.length !== 1 ? 's' : ''} shown
          </p>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}